import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import {
  PhonicsActivity,
  PhonicsActivityTypeMode,
  PhonicsEventMeta,
} from '@/api/types';
import { useLessonPlayer } from '@/context/LessonPlayer';
import {
  RoundButtonHandle,
  RoundHelpButton,
  CarouselSlide,
} from '@/components';
import { Stage } from '@/components/lessonKit';
import { getActivityStage } from './activityStage';

const DEV_MODE = false;
const DEV_MODE_INDENTS = 2;

type ActivityStepBgColor = 'yellow' | 'red';

// The v4 pedagogical stage colours (one activity = one full-bleed colour).
const STAGE_BG: Record<Stage, string> = {
  action: 'bg-[var(--c-action)]',
  select: 'bg-[var(--c-select)]',
  practice: 'bg-[var(--c-practice)]',
  progress: 'bg-[var(--c-progress)]',
  write: 'bg-[var(--c-write)]',
};

// Faint white radial "spotlights" that give each coloured stage depth.
const STAGE_SPOTLIGHTS: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  backgroundImage:
    'radial-gradient(circle at 12% 18%, rgba(255,255,255,0.07) 0, transparent 200px),' +
    'radial-gradient(circle at 88% 78%, rgba(255,255,255,0.07) 0, transparent 200px),' +
    'radial-gradient(circle at 60% 10%, rgba(255,255,255,0.05) 0, transparent 250px)',
};

interface ActivityStepSkeletonProps {
  className?: string;
  innerClassName?: string;
  identifier: string;
  ariaLabel?: string;
  bgColor?: ActivityStepBgColor;
  bgOrnament?: boolean;
  stage?: Stage;
}

export const ActivityStepSkeleton = ({
  children,
  className,
  innerClassName,
  identifier,
  ariaLabel,
  bgColor = 'yellow',
  bgOrnament = false,
  stage,
}: React.PropsWithChildren<ActivityStepSkeletonProps>) => (
  <CarouselSlide
    identifier={identifier}
    className={clsx(
      'relative flex flex-col justify-center items-center py-2 px-2 md:py-3 md:px-6 lg:px-16',
      // Reserve room for the fixed smartboard chrome (top status bar + bottom
      // control bar) so activity content never sits under it.
      stage && 'pt-28 pb-24 md:pt-32 md:pb-28',
      stage
        ? STAGE_BG[stage]
        : clsx(
            bgColor === 'yellow' && 'bg-yellow-normal',
            bgColor === 'yellow' &&
              bgOrnament &&
              "bg-[url('/phonics/images/decorations/yellow_leaves.png')] bg-no-repeat bg-left-bottom",
            bgColor === 'red' && 'bg-pt-coral-red',
          ),
      className,
    )}
    classNameInner={clsx(
      'flex flex-col gap-2 md:gap-4 w-full h-full',
      innerClassName,
    )}
    ariaLabel={ariaLabel}
    useContainer={false}
  >
    {stage && <div style={STAGE_SPOTLIGHTS} />}
    {children}
  </CarouselSlide>
);

export { getActivityStage };

export interface ActivityStepProps extends React.PropsWithChildren {
  className?: string;
  stepIdentifier: string;
  activity: PhonicsActivity;
  mode: PhonicsActivityTypeMode;
  stepNumber: number;
  totalSteps: number;
  wrapperClassName?: string;
  bgColor?: ActivityStepBgColor;
  bgOrnament?: boolean;
  usePanel?: boolean;
  title?: string;
  showTitle?: boolean;
  noBottomPadding?: boolean;
  showActivityPagination?: boolean;
  align?: 'center' | 'top' | 'bottom';
  onBeforeLeave?: () => boolean;
  onSlide?: () => void;
  onSlideSettle?: () => void;
  getActivityResponseMetaData?: () => PhonicsEventMeta;
  isPreview?: boolean;
}

const getHintTextForSlide = (activity: PhonicsActivity, stepNumber = 1) => {
  if (!activity.type.description?.includes('|')) {
    return activity.type.description || undefined;
  }

  const descriptions = activity.type.description?.split('|');

  return descriptions && descriptions[stepNumber - 1] !== undefined
    ? descriptions[stepNumber - 1]
    : undefined;
};

export interface ActivityStepHandle {
  showHelpTooltip: () => void;
  hideHelpTooltip: () => void;
}

export const ActivityStep = forwardRef<
  ActivityStepHandle,
  React.PropsWithChildren<ActivityStepProps>
>(
  (
    {
      children,
      className,
      stepIdentifier,
      activity,
      mode,
      stepNumber,
      totalSteps,
      wrapperClassName,
      bgColor = 'yellow',
      bgOrnament = false,
      usePanel = false,
      title,
      showTitle = true,
      noBottomPadding = false,
      showActivityPagination = true,
      align = 'center',
      onBeforeLeave,
      onSlide,
      onSlideSettle,
      getActivityResponseMetaData,
      isPreview,
    },
    ref,
  ) => {
    const helpButtonRef = useRef<RoundButtonHandle | null>(null);
    const { registerSlide } = useLessonPlayer();

    const stage = getActivityStage(
      activity.type.identifier,
      activity.type.mode,
    );

    useImperativeHandle(ref, () => ({
      showHelpTooltip: () => helpButtonRef.current?.showTooltip(),
      hideHelpTooltip: () => helpButtonRef.current?.hideTooltip(),
    }));

    useEffect(() => {
      const progressPercentage = Math.round((stepNumber / totalSteps) * 100);

      registerSlide(
        stepIdentifier,
        getActivityResponseMetaData,
        onBeforeLeave,
        onSlide,
        onSlideSettle,
        progressPercentage,
      );
    }, [
      registerSlide,
      stepIdentifier,
      getActivityResponseMetaData,
      onBeforeLeave,
      onSlide,
      onSlideSettle,
      stepNumber,
      totalSteps,
      activity,
    ]);

    return (
      <ActivityStepSkeleton
        key={stepIdentifier}
        identifier={stepIdentifier}
        className={wrapperClassName}
        ariaLabel={`${activity.type.name} - ${stepNumber} of ${totalSteps}`}
        bgColor={bgColor}
        bgOrnament={bgOrnament}
        stage={stage}
      >
        {showTitle && (
          <h1
            className="pt-1 pb-1 md:py-2 text-4xl md:text-5xl text-center uppercase tracking-wide text-white"
            style={{ fontFamily: 'var(--lp-display), Impact, sans-serif' }}
          >
            {title ? title : activity.type.name}
            <br />
            {isPreview ? (
              <span className="opacity-50">Preview mode</span>
            ) : null}
          </h1>
        )}

        <div
          className={clsx(
            'flex flex-col gap-2 md:gap-4 items-center text-center w-full h-full',
            usePanel && 'rounded-xl p-1 md:p-2 bg-white shadow-md',
            noBottomPadding && 'pb-2 md:pb-2',
            align === 'center' && 'justify-center',
            align === 'top' && 'justify-start',
            align === 'bottom' && 'justify-end',
            className,
          )}
          data-identifier={activity.id}
        >
          {showActivityPagination && (
            <p className="my-1 md:my-2 font-bold uppercase tracking-wider text-white/80">
              {stepNumber} of {totalSteps}
            </p>
          )}

          {children}

          {mode === PhonicsActivityTypeMode.TEACH &&
            activity.type.description && (
              <div className="absolute bottom-6 right-6">
                <RoundHelpButton
                  ref={helpButtonRef}
                  size="xs"
                  tooltipText={getHintTextForSlide(activity, stepNumber)}
                  tooltipPosition="leftBottom"
                  tooltipTrigger="click"
                />
              </div>
            )}

          {DEV_MODE && (
            <div className="absolute bottom-6 left-6 max-w-96 p-3 overflow-x-auto bg-white text-xs text-gray-500 text-left">
              <h2 className="font-bold">Dev info</h2>
              <pre>{JSON.stringify(activity, null, DEV_MODE_INDENTS)}</pre>
            </div>
          )}
        </div>
      </ActivityStepSkeleton>
    );
  },
);

ActivityStep.displayName = 'ActivityStep';

export interface ActivityProps {
  steps: Array<React.ReactElement<ActivityStepProps>>;
}

export const Activity = ({ steps }: ActivityProps) => <>{steps}</>;
